<template>
  <div class="v2-tabs">
    <button
      v-for="tab in tabs"
      :key="tab.key"
      class="v2-tabs__tab"
      :class="{ 'v2-tabs__tab--active': activeTab === tab.key }"
      @click="$emit('tab-change', tab.key)"
    >
      {{ tab.label }}
      <!--
        Animated ping indicator — mirrors the Downloads red-dot UI from the
        PLG sidebar (a static red dot inside a `animate-ping` ring). Outer
        span pulses + fades, inner span stays solid so the dot is always
        visible. Markup parallels the Tailwind reference in
        `Sidebar.tsx :: renderNavItem` so both surfaces feel identical.
      -->
      <span
        v-if="tab.indicator"
        class="v2-tabs__indicator"
      >
        <span class="v2-tabs__indicator-ping" />
        <span class="v2-tabs__indicator-dot" />
      </span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'V2Tabs',
  props: {
    tabs: {
      type: Array,
      required: true,
    },
    activeTab: {
      type: String,
      default: '',
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-tabs {
  display: flex;
  // Subtle glass band — translucent fill + glass-stroke read as a surface
  // against the page mesh substrate without per-element `backdrop-filter`.
  // Page mesh is already blurred at the layout level; per-strip blur was
  // redundant compositing work.
  background: var(--v2-glass-surface-2);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: 1rem 1rem 0 0;
  margin-bottom: var(--v2-space-4);
  padding: 0 var(--v2-space-2);

  &__tab {
    padding: var(--v2-space-3) var(--v2-space-4);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-muted-foreground);
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
      color: var(--v2-foreground);
    }

    &--active {
      color: var(--v2-primary);
      border-bottom-color: var(--v2-primary);
    }
  }

  // Animated red-dot — same recipe as the Downloads nav indicator in the
  // PLG sidebar (Tailwind `animate-ping` over a solid red dot). The
  // outer ring scales out + fades on a 1s loop while the inner dot stays
  // visible, so the eye is drawn to it without flicker.
  &__indicator {
    position: relative;
    display: inline-flex;
    width: 0.625rem;   // 10px — matches PLG sidebar (Tailwind h-2.5)
    height: 0.625rem;
    margin-left: var(--v2-space-2);
    vertical-align: middle;
    flex-shrink: 0;
  }

  &__indicator-ping {
    position: absolute;
    inset: 0;
    background: #f87171; // red-400, same as PLG ring
    border-radius: 9999px;
    opacity: 0.75;
    animation: v2-tabs-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  &__indicator-dot {
    position: relative;
    display: inline-flex;
    width: 100%;
    height: 100%;
    background: #ef4444; // red-500, same as PLG dot
    border-radius: 9999px;
  }
}

@keyframes v2-tabs-ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

@media (prefers-reduced-transparency: reduce) {
  .v2-tabs {
    background: var(--v2-card);
    border-color: var(--v2-border);
    border-bottom: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md) var(--v2-radius-md) 0 0;
  }
}

// Respect users with vestibular / motion sensitivity. Disable the
// infinite ping animation and hide the pulsing outer ring entirely;
// the solid inner dot still renders so the indicator is conveyed
// without movement.
@media (prefers-reduced-motion: reduce) {
  .v2-tabs__indicator-ping {
    animation: none;
    display: none;
  }
}
</style>
